// /pack/:packId/inventory — list of unlocked-but-uncolored items waiting
// to be colored. The scene page also surfaces these (clickable slots in
// step 14), but a list view scans faster when the kid has a few queued up.
//
// Reuses the scene endpoint — same data, different layout. Filters down to
// isUnlocked && !isComplete client-side.

import { Info } from 'lucide-react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { assetUrl, isPackId, PACKS, type PackId, type SceneItem } from '@skena/schema'
import { fetchPackScene } from '../scene/sceneApi.ts'

const PACK_BG: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-bg)]',
  nativity: 'bg-[var(--color-nativity-bg)]',
}
const PACK_BUTTON: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-primary)] text-white',
  nativity: 'bg-[var(--color-nativity-primary)] text-white',
}

export function InventoryPage() {
  const { packId } = useParams<{ packId: string }>()
  if (!packId || !isPackId(packId)) {
    return <Navigate to="/" replace />
  }
  return <InventoryInner packId={packId} />
}

function InventoryInner({ packId }: { packId: PackId }) {
  const pack = PACKS[packId]
  const sceneQuery = useQuery({
    queryKey: ['pack-scene', packId],
    queryFn: () => fetchPackScene(packId),
    staleTime: 30 * 1000,
  })

  const allItems = sceneQuery.data?.items ?? []
  const queue = allItems.filter((i) => i.isUnlocked && !i.isComplete)
  const colored = allItems.filter((i) => i.isComplete && i.artworkUrl && i.artworkId)

  return (
    <main
      className={`mx-auto flex min-h-full max-w-2xl flex-col gap-6 p-6 ${PACK_BG[packId]}`}
      data-pack={packId}
    >
      <header className="flex items-baseline justify-between">
        <Link
          to={`/pack/${packId}`}
          className="text-sm text-[var(--color-muted)] underline-offset-4 hover:underline"
        >
          ← 回 {pack.nameZh}
        </Link>
        <p className="text-xs text-[var(--color-muted)]">
          {queue.length > 0 ? `${queue.length} 張等你上色` : ''}
        </p>
      </header>

      <h1 className="font-[family-name:var(--font-display)] text-2xl text-[var(--color-fg)]">
        我的線條稿
      </h1>

      {sceneQuery.isLoading ? (
        <p className="text-sm text-[var(--color-muted)]">準備中…</p>
      ) : queue.length === 0 && colored.length === 0 ? (
        <EmptyState packId={packId} />
      ) : (
        <>
          {queue.length > 0 && (
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {queue.map((item) => (
                <li key={item.itemId}>
                  <QueueCard item={item} packId={packId} />
                </li>
              ))}
            </ul>
          )}

          {colored.length > 0 && (
            <section className="mt-2">
              <h2 className="font-[family-name:var(--font-display)] text-lg text-[var(--color-fg)]">
                我的彩圖
              </h2>
              <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {colored.map((item) => (
                  <li key={item.itemId}>
                    <ColoredCard item={item} packId={packId} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </main>
  )
}

function ColoredCard({ item, packId }: { item: SceneItem; packId: PackId }) {
  // Defensive — colored filter already guarantees these, but TS doesn't know.
  if (!item.artworkUrl || !item.artworkId) return null
  return (
    <div className="group relative aspect-square overflow-hidden rounded-[var(--radius-soft)] border border-black/10 bg-white shadow-sm">
      <Link
        to={`/pack/${packId}/color/${item.itemId}`}
        className="block h-full w-full"
        aria-label={`${item.nameZh} — 看／繼續上色`}
      >
        <img
          src={item.artworkUrl}
          alt={item.nameZh}
          crossOrigin="anonymous"
          className="h-full w-full object-cover"
        />
      </Link>
      <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-[var(--color-bg)]/85 px-2 py-1 text-center text-[11px] font-medium text-[var(--color-fg)] backdrop-blur-sm">
        {item.nameZh}
      </span>
      <div className="absolute right-1.5 top-1.5">
        <InfoIconLink packId={packId} item={item} />
      </div>
    </div>
  )
}

function QueueCard({ item, packId }: { item: SceneItem; packId: PackId }) {
  return (
    <div className="group relative rounded-[var(--radius-soft)] border border-black/10 bg-white transition-shadow hover:shadow-md">
      <Link
        to={`/pack/${packId}/color/${item.itemId}`}
        className="flex flex-col gap-2 p-4"
      >
        <div className="aspect-[16/9] w-full overflow-hidden rounded-[6px] bg-black/5">
          <img
            src={assetUrl(packId, item.itemId, 'lineart')}
            alt={item.nameZh}
            className="h-full w-full object-contain transition-transform group-hover:scale-105"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).style.display = 'none'
            }}
          />
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-[family-name:var(--font-display)] text-base text-[var(--color-fg)]">
            {item.nameZh}
          </span>
          <span className="text-xs text-[var(--color-muted)]">{item.scriptureRef}</span>
        </div>
        <span
          className={`mt-1 self-start rounded-[var(--radius-pill)] px-3 py-1 text-xs font-semibold ${PACK_BUTTON[packId]}`}
        >
          上色
        </span>
      </Link>
      <div className="absolute right-2 top-2">
        <InfoIconLink packId={packId} item={item} />
      </div>
    </div>
  )
}

// Standalone info button that navigates to the item detail page. Used in
// both inventory card variants. stopPropagation so taps inside an outer
// Link (queue card or colored card) don't trigger the cell's main nav.
function InfoIconLink({ packId, item }: { packId: PackId; item: SceneItem }) {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        navigate(`/pack/${packId}/item/${item.itemId}`)
      }}
      aria-label={`${item.nameZh} — 看詳情`}
      className="rounded-full bg-white/90 p-2 text-[var(--color-fg)] shadow-sm transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <Info size={16} aria-hidden />
    </button>
  )
}

function EmptyState({ packId }: { packId: PackId }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[var(--radius-soft)] border-2 border-dashed border-[var(--color-muted)]/30 p-8 text-center">
      <p className="text-sm text-[var(--color-muted)]">
        還沒有線條稿等你上色——先去練習解鎖第一張吧
      </p>
      <Link
        to={`/pack/${packId}/practice`}
        className={`rounded-[var(--radius-pill)] px-5 py-2 text-sm font-semibold ${PACK_BUTTON[packId]}`}
      >
        開始練習
      </Link>
    </div>
  )
}
